import React from 'react';
import { motion } from 'framer-motion';

function getHealthDotColor(instanceStatus) {
  if (!instanceStatus) return 'rgba(255,255,255,0.3)'; // gray
  const { status, health_status } = instanceStatus;
  if (status === 'running' && (health_status === 'healthy' || health_status === 'unknown')) return '#2ecc71'; // green
  if (status === 'creating' || health_status === 'degraded') return '#f39c12'; // amber
  if (status === 'error' || status === 'stopped' || health_status === 'unhealthy') return '#ff3c3c'; // red
  if (status === 'no_instance') return 'rgba(255,255,255,0.3)'; // gray
  return 'rgba(255,255,255,0.3)'; // gray default
}

export default function TopBar({ mode, onModeChange, onSettingsOpen, audioPlaying, onAudioToggle, instanceStatus }) {
  const dotColor = getHealthDotColor(instanceStatus);
  const isGreen = dotColor === '#2ecc71';

  return (
    <motion.div
      data-testid="top-bar"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'calc(env(safe-area-inset-top, 12px) + 8px) 12px 10px',
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      {/* Left: Menu button with health dot */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <motion.button
          data-testid="menu-button"
          onClick={onSettingsOpen}
          style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'white', flexShrink: 0,
          }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="5" cy="12" r="2" fill="currentColor" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <circle cx="19" cy="12" r="2" fill="currentColor" />
          </svg>
        </motion.button>
        {/* Health dot badge */}
        <div
          data-testid="health-dot"
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            boxShadow: isGreen ? `0 0 6px ${dotColor}` : 'none',
            border: '1.5px solid rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Center: Mode toggle - clean glassmorphism */}
      <div
        data-testid="mode-toggle-glass"
        style={{
          position: 'relative',
          display: 'flex',
          gap: 4,
          padding: 4,
          borderRadius: 28,
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px) saturate(1.5)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Sliding pill */}
        <motion.div
          style={{
            position: 'absolute',
            top: 4,
            height: 'calc(100% - 8px)',
            width: 'calc(50% - 6px)',
            borderRadius: 24,
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
          }}
          animate={{ left: mode === 'casual' ? 4 : 'calc(50% + 2px)' }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        />
        {[
          { key: 'casual', label: 'Chat' },
          { key: 'agentic', label: 'Oi Agent' },
        ].map(({ key, label }) => (
          <button
            key={key}
            data-testid={`mode-${key}-btn`}
            onClick={() => onModeChange(key)}
            style={{
              position: 'relative', zIndex: 1,
              background: 'transparent', border: 'none',
              color: mode === key ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
              fontSize: 14, fontWeight: 600,
              padding: '10px 20px', borderRadius: 24,
              cursor: 'pointer', transition: 'color 0.3s ease',
              minWidth: 72,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Right: Audio button */}
      <motion.button
        data-testid="audio-button"
        onClick={onAudioToggle}
        style={{
          width: 44, height: 44, borderRadius: '50%',
          background: audioPlaying ? 'rgba(167,21,233,0.25)' : 'rgba(0,0,0,0.25)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${audioPlaying ? 'rgba(167,21,233,0.3)' : 'rgba(255,255,255,0.06)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'white', flexShrink: 0,
        }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <motion.rect x="4" y="8" width="2.5" rx="1.25" fill="currentColor"
            animate={{ height: audioPlaying ? [8, 12, 6, 10, 8] : 8 }}
            transition={{ duration: 0.8, repeat: audioPlaying ? Infinity : 0 }} />
          <motion.rect x="8.5" y="5" width="2.5" rx="1.25" fill="currentColor"
            animate={{ height: audioPlaying ? [14, 8, 12, 6, 14] : 14 }}
            transition={{ duration: 0.8, repeat: audioPlaying ? Infinity : 0, delay: 0.1 }} />
          <motion.rect x="13" y="9" width="2.5" rx="1.25" fill="currentColor"
            animate={{ height: audioPlaying ? [6, 10, 14, 8, 6] : 6 }}
            transition={{ duration: 0.8, repeat: audioPlaying ? Infinity : 0, delay: 0.2 }} />
          <motion.rect x="17.5" y="6" width="2.5" rx="1.25" fill="currentColor"
            animate={{ height: audioPlaying ? [12, 6, 8, 14, 12] : 12 }}
            transition={{ duration: 0.8, repeat: audioPlaying ? Infinity : 0, delay: 0.15 }} />
        </svg>
      </motion.button>
    </motion.div>
  );
}
